import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  BasicTeaserHeadlineSize,
  BasicTeaserSchemeDefinition,
  BasicTeaserSchemeName,
  BasicTeaserTextColor
} from "./constants/basic-teaser-types";
import { BASIC_TEASER_SCHEME_CONFIG, BASIC_TEASER_SCHEME_MAP } from "./constants/basic-teaser-constants";
import { HeadlineTag } from "../headline/constants/headline-types";
import { clampHeadlineSizeTo, useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { getFirstNestedChild, getNestedChildrenOf } from "../../utils/dom-helper";
import { createButtonMapDefinition } from "../../utils/scheme-helper";
import { BackgroundColor } from "../background/constants/background-types";
import * as GlobalConstants from "../../constants/global-constants";

const {
  BASIC_TEASER_COLORS,
  BASIC_TEASER_DEFAULT_CONTENT_ALIGNMENT,
  BASIC_TEASER_HEADLINE_SIZE,
  BASIC_TEASER_HEADLINE_SIZES
} = GlobalConstants;

const BASIC_TEASER_NO_BORDER =
  "BASIC_TEASER_NO_BORDER" in GlobalConstants ? GlobalConstants.BASIC_TEASER_NO_BORDER : false;

import { BasicTeaserSchemeConfiguration } from "../../constants/colors";
import { AspectRatio } from "../../common/types";
import { setCssVariable } from "../../utils/css-variables";
import { camelToDashCase } from "../../utils/string-format";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";
import { BasicTeaserContentAlignment } from "../../constants/global-constants";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamend] - Holds any number of HTML elements
 * @slot cta - Holds <eon-ui-button> or <eon-ui-link>.
 * @slot image - Holds <eon-ui-image>.
 */
@Component({
  tag: "eon-ui-basic-teaser",
  styleUrl: "basic-teaser.scss",
  shadow: true
})
export class BasicTeaser
  implements
    ColorableComponent<BasicTeaserSchemeConfiguration<BasicTeaserSchemeDefinition>>,
    ContainerBreakpointComponent {
  // @InitLogging() sentryTransaction;
  schemeConfig: BasicTeaserSchemeConfiguration<BasicTeaserSchemeDefinition> = BASIC_TEASER_SCHEME_CONFIG;
  schemeMap = BASIC_TEASER_SCHEME_MAP;
  private slottedHeadlines: Array<HTMLEonUiHeadlineElement>;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [1200, 800, 400];

  @State() activeScheme: BasicTeaserSchemeDefinition;
  @State() seoHeadlineTag: HeadlineTag;
  @State() isImageAvailable: boolean;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiBasicTeaserElement;

  private linkElement: HTMLEonUiLinkElement;
  private infoElement: HTMLEonUiInfoIconElement;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * Raised when the CTA in Basic Teaser has been clicked.
   */
  @Event() basicTeaserClick: EventEmitter;

  /**
   * Raised when the component has been loaded.
   */
  @Event() basicTeaserLoad: EventEmitter;

  /**
   * The color scheme of the Teaser
   */
  @Prop({ reflect: true }) scheme: BasicTeaserSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * The size of the headline inside the teaser
   */
  @Prop({ mutable: true }) size: BasicTeaserHeadlineSize = BASIC_TEASER_HEADLINE_SIZE;

  /**
   * Alignment of the content-box.
   * @example left | center | product
   */
  // prettier-ignore
  @Prop() contentAlignment: BasicTeaserContentAlignment = BASIC_TEASER_DEFAULT_CONTENT_ALIGNMENT;

  /**
   * Defines the aspect ratio.
   */
  @Prop({ mutable: true, reflect: true }) aspectRatio: AspectRatio;

  /**
   * Defines the aspect ratio for tablet breakpoint.
   */
  @Prop({ mutable: true, reflect: true }) aspectRatioTablet: AspectRatio;

  /**
   * The default (unhovered) text color of the teaser.
   */
  @Prop({ reflect: true }) textColor: BasicTeaserTextColor = BASIC_TEASER_COLORS[0];

  /**
   * Defines the aspect ratio for mobile breakpoint.
   */
  @Prop({ mutable: true, reflect: true }) aspectRatioMobile: AspectRatio;

  /**
   * The text of the eon-ui-headline inside the teaser (see eon-ui-headline)
   */
  @Prop() headlineText: string;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline
   *
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * The text of the subheadline inside the teaser
   */
  @Prop() subheadlineText: string;

  /**
   * name of the eon-ui-pictogram inside the teaser (see eon-ui-pictogram)
   */
  @Prop() pictogram: string;

  /**
   * Display image as background
   */
  @State() imageAsBackground: boolean = false;

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  componentWillLoad() {
    this.linkElement =
      getFirstNestedChild(this.hostElement, "eon-ui-link") || getFirstNestedChild(this.hostElement, "eon-ui-button");
    this.infoElement = getFirstNestedChild(this.hostElement, "eon-ui-info-icon");
    this.slottedHeadlines = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-headline"));
    this.slottedHeadlines.forEach((headline) => {
      headline.setAttribute("ignore-alignment", "true");
    });
    this.applyScheme();
    this.initContainerBreakpoints();
    this.passSchemeToChildren();
    this.handleHeadlineTag();
    this.handleSlottedImage();
    this.infoElement?.addEventListener("click", (e) => {
      e.preventDefault();
    });
  }

  componentDidLoad() {
    this.setCssVariables();
    this.applyTextColor();
    this.basicTeaserLoad.emit();
  }

  @Listen("buttonClick")
  @Listen("linkClick")
  handleCTAOnClick(event) {
    this.basicTeaserClick.emit(event);
  }

  @Watch("textColor")
  applyTextColor() {
    setCssVariable(this.hostElement, "--textColor", `var(--eon-${camelToDashCase(this.textColor)})`);
    this.imageAsBackground && this.infoElement?.setAttribute("scheme", this.textColor);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("scheme")
  // This method is responsible to keep slotted components in sync with page-header scheme
  // when it changes during the life of the component. This mostly applies to storybook controls in
  // canvas tab where changes to props don't trigger component reloading (In docs tab it does).
  passSchemeToChildren() {
    if (!!this.scheme) {
      const backgroundColor = this.innerBackground;
      const buttonSchemeMapDefinition = createButtonMapDefinition();
      let buttons = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-button"));
      buttons.forEach((slotButton) =>
        slotButton.setAttribute("scheme", buttonSchemeMapDefinition[backgroundColor as BackgroundColor] ?? "")
      );
    }
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.contentBackgroundColor;
  }

  @Watch("size")
  clampSize() {
    this.size = clampHeadlineSizeTo(BASIC_TEASER_HEADLINE_SIZES, this.size) as BasicTeaserHeadlineSize;
  }

  @Watch("headlineHtmlTag")
  @Watch("size")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, this.size);
  }

  @Listen("mouseenter")
  onMouseEnter() {
    if (this.linkElement) this.linkElement.setAttribute("force-hover-style", String(true));
  }

  @Listen("mouseleave")
  onMouseLeave() {
    if (this.linkElement) this.linkElement.setAttribute("force-hover-style", String(false));
  }

  handleSlottedImage() {
    let slottedImage = getFirstNestedChild(this.hostElement, "eon-ui-image") as HTMLEonUiImageElement;
    this.isImageAvailable = !!slottedImage;
    this.imageAsBackground = slottedImage?.useAsBackground || false;
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "basic-teaser-wrapper": true,
            gradient: this.activeScheme.gradientColor // E.ON specific. Only set if gradientColor variable of a scheme is defined.
          })}
        >
          <a
            class={pfx("basic-teaser")}
            href={this.linkElement?.href || "#"}
            target={this.linkElement?.target}
            title={this.linkElement?.title}
          >
            <div
              class={pfx({
                wrapper: true,
                "image-as-background": this.imageAsBackground,
                "hover-overlay": !BASIC_TEASER_NO_BORDER ? this.imageAsBackground : false,
                [`${this.aspectRatio}`]: !!this.aspectRatio && this.imageAsBackground,
                [`tablet-${this.aspectRatioTablet}`]: !!this.aspectRatioTablet && this.imageAsBackground,
                [`mobile-${this.aspectRatioMobile}`]: !!this.aspectRatioMobile && this.imageAsBackground,
                gradient: this.activeScheme.gradientColor // E.ON specific. Only set if gradientColor variable of a scheme is defined.
              })}
            >
              {<slot name="image"></slot>}
              <div class={pfx({ overlay: true })}></div>
              <div
                class={pfx({
                  content: true,
                  [`content-alignment-${this.contentAlignment}`]: true
                })}
              >
                <div
                  class={pfx({
                    ["content-wrapper"]: true
                  })}
                >
                  <div
                    class={pfx({
                      ["header-wrapper"]: true
                    })}
                  >
                    {this.pictogram && (
                      <div
                        class={pfx({
                          "pictogram-wrapper": true
                        })}
                      >
                        <eon-ui-pictogram name={this.pictogram}></eon-ui-pictogram>
                      </div>
                    )}
                    {this.subheadlineText && (
                      <eon-ui-text
                        class={pfx({
                          subheadline: true
                        })}
                      >
                        {this.subheadlineText}
                      </eon-ui-text>
                    )}
                    {this.headlineText && (
                      <eon-ui-headline
                        class={pfx({
                          ["no-pictogram"]: !this.pictogram
                        })}
                        margin={false}
                        size={this.size}
                        headlineHtmlTag={this.seoHeadlineTag}
                        ignoreAlignment={true}
                        text={this.headlineText}
                      ></eon-ui-headline>
                    )}
                    <div
                      class={pfx({
                        ["text-wrapper"]: true,
                        [`alignment-${this.contentAlignment}`]: true
                      })}
                    >
                      <slot name="text" />
                      <slot></slot>
                    </div>
                  </div>
                  <div
                    class={pfx({
                      ["cta-wrapper"]: true
                    })}
                  >
                    <slot name="cta"></slot>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>
      </Host>
    );
  }
}
